<template>
  <div v-show="showMedia">
    <img
      v-if="isImage"
      :style="imageDimensions"
      :alt="alt"
      :src="src"
      :class="[
        'attachment-image dash-rounded-sm',
        {
          'tight-fit': tightFit,
        },
      ]"
      @load="mediaLoaded"
    />
    <video
      v-else-if="isVideo"
      :style="videoDimensions"
      :src="src"
      :class="[
        'attachment-video dash-rounded-sm',
        {
          'tight-fit': tightFit,
        },
      ]"
      controls="controls"
      controlslist="nodownload"
      @loadeddata="mediaLoaded"
    />
  </div>
</template>

<script>
import { defineComponent } from 'vue';

import { COMMUNITY_SUPPORTED_MEDIA_TYPES } from '@/app/community/constants';

const comp = defineComponent({
  name: 'Attachment',
  props: {
    mediaType: { type: String, default: null },
    src: { type: String, default: '' },
    alt: { type: String, default: '' },
    displayWidth: { type: String, default: '' },
    displayHeight: { type: String, default: '' },
    tightFit: { type: Boolean, default: false },
  },
  emits: ['mediaLoaded'],

  data() {
    return {
      showMedia: false,
      width: null,
      height: null,
    };
  },
  computed: {
    isImage() {
      return [
        COMMUNITY_SUPPORTED_MEDIA_TYPES.IMAGE,
        COMMUNITY_SUPPORTED_MEDIA_TYPES.PHOTO,
      ].includes(this.mediaType);
    },
    isVideo() {
      return this.mediaType === COMMUNITY_SUPPORTED_MEDIA_TYPES.VIDEO;
    },
    isSquare() {
      if (this.width && this.height) {
        const ratio = this.width / this.height;
        return ratio < 1.2 && ratio > 0.8;
      }
      return false;
    },
    imageDimensions() {
      let result = {};
      if (this.width && this.height) {
        if (this.isSquare) {
          result = {
            maxHeight: '16.667rem',
            maxWidth: '16.667rem',
          };
        } else {
          result =
            this.width > this.height
              ? {
                  height: '11rem',
                  maxWidth: '16.5rem',
                }
              : {
                  height: '15rem',
                  maxWidth: '8.45rem',
                };
        }
      }

      if (this.displayWidth) {
        result.width = this.displayWidth;
      }
      if (this.displayHeight) {
        result.height = this.displayHeight;
      }
      return result;
    },
    videoDimensions() {
      let result = {};
      if (this.width && this.height) {
        if (this.isSquare) {
          result = {
            maxHeight: '16.667rem',
            maxWidth: '16.667rem',
          };
        } else {
          result =
            this.width > this.height
              ? {
                  height: '9.375rem',
                  maxWidth: '16.667rem',
                }
              : {
                  height: '20rem',
                  maxWidth: '11.25rem',
                };
        }
      }

      if (this.displayWidth) {
        result.width = this.displayWidth;
      }
      if (this.displayHeight) {
        result.height = this.displayHeight;
      }
      return result;
    },
  },
  async created() {
    if (this.isImage) {
      const metaData = await this.getImageDimensions();
      [this.width, this.height] = metaData;
    } else {
      const metaData = await this.getVideoDimensions();
      [this.width, this.height] = metaData;
    }
  },
  methods: {
    mediaLoaded() {
      this.showMedia = true;
      this.$emit('mediaLoaded');
    },
    getImageDimensions() {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve([img.width, img.height]);
        img.onerror = () => reject();
        img.src = this.src;
      });
    },
    getVideoDimensions() {
      return new Promise((resolve) => {
        const video = document.createElement('video');
        video.addEventListener(
          'loadedmetadata',
          function callback() {
            resolve([this.videoWidth, this.videoHeight]);
          },
          false,
        );

        video.src = this.src;
      });
    },
  },
});
export default comp;
</script>

<style lang="postcss" scoped>
.attachment-image {
  height: 11rem;
  max-width: 16.5rem; /* no wider than a 3:2 aspect ratio */
}

.attachment-video {
  height: 9.375rem; /* video width's can scale too wide if height is increased */
  max-width: 16.667rem; /* no wider than a 16:9 aspect ratio for video */
}

*[class^='attachment-'] {
  width: auto;
  object-fit: fill;
  display: block;
  margin-top: var(--space-12);
  margin-bottom: var(--space-4);

  &.tight-fit {
    margin: 0;
  }
}
</style>
